import { CHIP_NO_RECENT_QUEUE_SEND } from '@shared/flow/contact-view';
import { viewDefinitionsEqual } from '@shared/flow/view-normalize';
import type { FlowView } from '@shared/flow/view';
import type { FlowWorkingState } from '@shared/flow/view-state';
import type { FilterGroup } from '@shared/flow/filter';
import { stripSyntheticColumns } from '../views/syntheticColumns';
import type { FlowViewStateStore } from '../views/useViewState';
import type { ContactSurface } from './useContactMount';
import { CONTACT_PLAY_CANDIDATES_PRESET_ID, CONTACT_PRESETS } from '@shared/flow/contact-view';

/**
 * D5 (plan 2026-09-25-004, carried forward unmodified by the 2026-09-25 PM
 * simplification — `roleDefaults.ts`'s D1/D2/D3): "Not in this play yet"
 * used to seed the role-independent L30 chip (`CHIP_NO_RECENT_QUEUE_SEND`);
 * it now seeds the Mine group instead (`buildPlayMemberMineGroup`). A rep's
 * STORED `contact_play_candidates` working state does not know that — it is
 * whatever `adaptOnOpen` produced the last time this section persisted for
 * them. Without this module, a rep who never touched the section's filters
 * would keep seeing the OLD default (the L30 chip) forever, reading as
 * permanent "Modified" against the new baseline the moment `roleDefaults.ts`
 * changed under them.
 *
 * The fix is a pure client-side compare, not a server migration: on load,
 * IF the stored row is byte-equivalent (mod ids/widths) to what the OLD
 * `adaptOnOpen` produced for the landing preset, THEN the machine is handed
 * a null working state so both restore rungs (`useViewStateMachine.ts`)
 * land on the FRESHLY adapted baseline — which today means the Mine group.
 * Any other stored row (a real edit — a different sort, an extra chip, the
 * L30 chip removed) is restored verbatim and reads Modified against the new
 * baseline, exactly as it always has. No write happens until the rep's next
 * edit; re-detecting on every load is idempotent.
 */
export const LEGACY_PLAY_CANDIDATES_SEED: readonly FilterGroup[] = [CHIP_NO_RECENT_QUEUE_SEND];

/**
 * True iff `stored` is exactly what the OLD `adaptOnOpen` produced for the
 * candidates landing preset: the preset's own filter (the alumni exclusion)
 * plus the L30 chip, AND, the preset's own sort/groupBy/columns (with the
 * synthetic Terra Actions column inserted, same as every other opened view).
 * Ids and column widths are irrelevant — `viewDefinitionsEqual` ignores both.
 */
export function isLegacyPlayCandidatesDefault(
  stored: FlowWorkingState | null | undefined,
  preset: FlowView,
): boolean {
  if (!stored) return false;
  return viewDefinitionsEqual(
    { ...stored, columns: stripSyntheticColumns(stored.columns) },
    {
      filter: [...preset.filter, ...LEGACY_PLAY_CANDIDATES_SEED],
      groupLogic: 'AND',
      sort: preset.sort,
      groupBy: preset.groupBy,
      columns: preset.columns,
    },
  );
}

/**
 * Wraps a `FlowViewStateStore` BEFORE `useViewStateMachine` sees it (D5):
 * same reference unless the surface is `'play-candidates'` AND the stored
 * row is the exact legacy default for the candidates preset — then the
 * restored `workingState` is nulled out, so both of the machine's restore
 * rungs (the `?view=` param rung and the stored-active-id rung) fall
 * through to the freshly adapted baseline instead of replaying the L30 seed
 * forever.
 */
export function withoutLegacyPlayCandidatesWorkingState(
  store: FlowViewStateStore,
  surface: ContactSurface,
): FlowViewStateStore {
  if (surface !== 'play-candidates') return store;
  const restored = store.restored;
  if (!restored || restored.activeViewId !== CONTACT_PLAY_CANDIDATES_PRESET_ID) return store;
  const preset = CONTACT_PRESETS.find(p => p.id === CONTACT_PLAY_CANDIDATES_PRESET_ID);
  if (!preset) return store;
  if (!isLegacyPlayCandidatesDefault(restored.workingState, preset)) return store;
  return { ...store, restored: { ...restored, workingState: null } };
}
